import { useEffect, useSyncExternalStore } from 'react';
import { versionWatch } from './version';
import type { VersionWatch } from './version';

export const NEW_VERSION_TEXT = 'A new version of Slimez is ready';
export const NEW_VERSION_BLOCKING_HINT = 'Reload to play — this tab still runs the old one.';

const reloadPage = () => {
  if (typeof location !== 'undefined') location.reload();
};

/** A running match holds the banner back until it ends (W032: never over a turn). */
export function useVersionHold(active: boolean, watch: VersionWatch = versionWatch): void {
  useEffect(() => (active ? watch.holdPrompt() : undefined), [active, watch]);
}

/**
 * The page's "new version" prompt (W032, `version.ts`): a small banner with "Reload" once the
 * server runs another release (not while a match runs), or a blocking dialog once a create / join
 * was refused for it. Mounted once at the app root; it also starts the page's checks.
 */
export function NewVersionPrompt({
  watch = versionWatch,
  onReload = reloadPage,
}: {
  readonly watch?: VersionWatch;
  readonly onReload?: () => void;
}) {
  useEffect(() => watch.start(), [watch]);
  // "Reload" remembers the server build it reloads for (W032-R m2: a futile reload stops asking).
  const reload = () => {
    watch.markReloading();
    onReload();
  };
  const state = useSyncExternalStore(watch.subscribe, watch.getSnapshot, watch.getSnapshot);
  if (!state.stale) return null;
  if (state.blocking) {
    return (
      <div className="app-update app-update--blocking" data-testid="app-update">
        <div
          className="app-update__dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="app-update-title"
        >
          <strong id="app-update-title">{NEW_VERSION_TEXT}</strong>
          <span className="app-update__hint">{NEW_VERSION_BLOCKING_HINT}</span>
          <button
            type="button"
            className="app-update__reload"
            onClick={reload}
            data-testid="app-update-reload"
            autoFocus
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
  if (state.held) return null;
  return (
    <div className="app-update" role="status" data-testid="app-update">
      <span>{NEW_VERSION_TEXT}</span>
      <button
        type="button"
        className="app-update__reload"
        onClick={reload}
        data-testid="app-update-reload"
      >
        Reload
      </button>
    </div>
  );
}
